import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ETLStatus {
  entity: string;
  action: string;
  details: any;
  created_at: string;
}

interface PortfolioPerformance {
  portfolio_id: string;
  portfolio_name: string;
  org_id: string;
  last_valuation_at: string | null;
  last_irr: number | null;
  last_npv: number | null;
  lifetime_collections: number;
  nominal_value: number | null;
}

interface WarehouseState {
  etlStatus: ETLStatus[];
  portfolioPerformance: PortfolioPerformance[];
  loading: boolean;
  loadETLStatus: () => Promise<void>;
  loadPortfolioPerformance: () => Promise<void>;
  runDimensions: () => Promise<void>;
  runFactsIncremental: () => Promise<void>;
  refreshMarts: () => Promise<void>;
}

export const useWarehouseStore = create<WarehouseState>((set, get) => ({
  etlStatus: [],
  portfolioPerformance: [],
  loading: false,

  loadETLStatus: async () => {
    set({ loading: true });
    try {
      const { data, error } = await (supabase as any)
        .from('events')
        .select('*')
        .in('entity', ['dw.dimensions', 'dw.facts', 'amart.portfolio_performance'])
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      set({ etlStatus: data || [] });
    } catch (error) {
      console.error('Failed to load ETL status:', error);
      toast.error('Failed to load ETL status');
    } finally {
      set({ loading: false });
    }
  },

  loadPortfolioPerformance: async () => {
    set({ loading: true });
    try {
      // Query the materialized view directly
      const { data, error } = await (supabase as any)
        .from('portfolio_performance')
        .select('*')
        .order('last_valuation_at', { ascending: false, nullsFirst: false })
        .limit(10);

      if (error) throw error;
      set({ portfolioPerformance: data || [] });
    } catch (error) {
      console.error('Failed to load portfolio performance:', error);
      toast.error('Failed to load portfolio performance');
    } finally {
      set({ loading: false });
    }
  },

  runDimensions: async () => {
    set({ loading: true });
    try {
      const { error } = await supabase.functions.invoke('etl-load-dimensions');
      if (error) throw error;
      toast.success('Dimensions ETL completed successfully');
      await get().loadETLStatus();
    } catch (error) {
      console.error('Failed to run dimensions ETL:', error);
      toast.error('Failed to run dimensions ETL');
    } finally {
      set({ loading: false });
    }
  },

  runFactsIncremental: async () => {
    set({ loading: true });
    try {
      const { error } = await supabase.functions.invoke('etl-load-facts-incremental');
      if (error) throw error;
      toast.success('Facts ETL completed successfully');
      await get().loadETLStatus();
    } catch (error) {
      console.error('Failed to run facts ETL:', error);
      toast.error('Failed to run facts ETL');
    } finally {
      set({ loading: false });
    }
  },

  refreshMarts: async () => {
    set({ loading: true });
    try {
      const { error } = await supabase.functions.invoke('etl-refresh-marts');
      if (error) throw error;
      toast.success('Marts refreshed successfully');
      await Promise.all([get().loadETLStatus(), get().loadPortfolioPerformance()]);
    } catch (error) {
      console.error('Failed to refresh marts:', error);
      toast.error('Failed to refresh marts');
    } finally {
      set({ loading: false });
    }
  },
}));
